import type { NoteColor } from '@/enums/noteColor';
import { isNoteColor } from '@/enums/noteColor';

const SESSION_KEY = 'notes:session:v1';

interface SessionState {
  lastColor?: NoteColor;
}

/**
 * Transient per-tab UI state. Intentionally not persisted across sessions so
 * cross-session artefacts (last-selected color from a stale tab) don't leak.
 */
export class SessionStore {
  constructor(private readonly storage: Storage = safeSessionStorage()) {}

  get lastColor(): NoteColor | undefined {
    return this.read().lastColor;
  }

  setLastColor(color: NoteColor): void {
    this.write({ ...this.read(), lastColor: color });
  }

  private read(): SessionState {
    const raw = this.storage.getItem(SESSION_KEY);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed !== 'object' || parsed === null) return {};
      const p = parsed as Record<string, unknown>;
      const state: SessionState = {};
      if (isNoteColor(p['lastColor'])) state.lastColor = p['lastColor'];
      return state;
    } catch {
      return {};
    }
  }

  private write(state: SessionState): void {
    try {
      this.storage.setItem(SESSION_KEY, JSON.stringify(state));
    } catch {
      // best-effort
    }
  }
}

function safeSessionStorage(): Storage {
  try {
    return globalThis.sessionStorage;
  } catch {
    const map = new Map<string, string>();
    return {
      get length() {
        return map.size;
      },
      key: (i: number) => [...map.keys()][i] ?? null,
      getItem: (k) => (map.has(k) ? (map.get(k) ?? null) : null),
      setItem: (k, v) => {
        map.set(k, v);
      },
      removeItem: (k) => {
        map.delete(k);
      },
      clear: () => {
        map.clear();
      },
    };
  }
}
